import {
	isDocumentContent,
	isImageContent,
	isToolResultContent,
	isToolUseContent,
	isUploadedFileContent,
	type ChatMessageDto,
	type DocumentContentBlock,
	type ImageContentBlock,
	type ToolUseContentBlock,
} from "@/lib/chat";
import { fromUploadedFileBlock } from "@/lib/uploaded-file-block";

/** モデル入力から外す画像ブロックの位置。tool_result にネストした画像は nestedIndex でその content 配列内の位置を指す。 */
export interface ImagePlaceholderTarget {
	messageId: string;
	blockIndex: number;
	nestedIndex: number | null;
}

/** 位置を集合・Map のキーにするための文字列。 */
export function placeholderKey(target: ImagePlaceholderTarget): string {
	return `${target.messageId}#${target.blockIndex}#${target.nestedIndex ?? ""}`;
}

/**
 * モデル入力に載ったファイル 1 件。画像はサムネイル描画のため元ブロックを、置き換えのため位置を持つ。
 * ワークスペースへ置いた添付は中身ではなくパスだけがモデル入力に載る。
 */
export type ChatFile =
	| {
			kind: "image";
			key: string;
			name: string;
			mediaType: string;
			byteSize: number;
			image: ImageContentBlock;
			target: ImagePlaceholderTarget;
	  }
	| {
			kind: "document";
			key: string;
			name: string;
			mediaType: string;
			byteSize: number;
	  }
	| {
			kind: "uploaded";
			key: string;
			name: string;
			/** `~/` から始まるワークスペース上のパス。 */
			path: string;
			byteSize: number;
	  };

const EXTENSION_BY_MEDIA_TYPE: Record<string, string> = {
	"image/png": ".png",
	"image/jpeg": ".jpg",
	"image/gif": ".gif",
	"image/webp": ".webp",
	"application/pdf": ".pdf",
};

/** base64 文字列が表すバイナリのバイト数。 */
export function base64ByteLength(data: string): number {
	const padding = data.endsWith("==") ? 2 : data.endsWith("=") ? 1 : 0;
	return Math.floor((data.length * 3) / 4) - padding;
}

function extensionOf(mediaType: string): string {
	return EXTENSION_BY_MEDIA_TYPE[mediaType] ?? "";
}

function baseName(path: string): string {
	return path.split("/").findLast((segment) => segment.length > 0) ?? path;
}

/** view_image 等、path 入力を持つツール呼び出しからファイル名を取り出す。 */
function toolInputFileName(toolUse: ToolUseContentBlock | undefined): string | null {
	const path = toolUse?.input.path;
	return typeof path === "string" && path.length > 0 ? baseName(path) : null;
}

function imageFile(
	key: string,
	name: string,
	image: ImageContentBlock,
	target: ImagePlaceholderTarget,
): ChatFile {
	return {
		kind: "image",
		key,
		name,
		mediaType: image.source.media_type,
		byteSize:
			image.source.type === "url" ? image.source.byte_size : base64ByteLength(image.source.data),
		image,
		target,
	};
}

export function documentByteLength(block: DocumentContentBlock): number {
	return block.source.type === "omitted"
		? block.source.byte_size
		: block.source.type === "base64"
			? base64ByteLength(block.source.data)
			: new TextEncoder().encode(block.source.data).length;
}

function documentFile(key: string, block: DocumentContentBlock): ChatFile {
	return {
		kind: "document",
		key,
		name: block.title || `文書${extensionOf(block.source.media_type)}`,
		mediaType: block.source.media_type,
		byteSize: documentByteLength(block),
	};
}

/**
 * チャットの履歴からモデル入力に載ったファイル (ユーザー添付の画像・文書・ワークスペースへ置いたファイル、
 * ツールが読み込んだ画像・文書) を
 * 時系列で集める。最後の compaction より前の行はモデル入力から外れているため対象にしない。
 */
export function collectChatFiles(messages: readonly ChatMessageDto[]): ChatFile[] {
	const start = messages.findLastIndex((message) => message.role === "compaction") + 1;
	const toolUseById = new Map<string, ToolUseContentBlock>();
	const files: ChatFile[] = [];
	let attachedImageCount = 0;
	for (const message of messages.slice(start)) {
		for (const [blockIndex, block] of message.content.entries()) {
			if (isToolUseContent(block)) {
				toolUseById.set(block.id, block);
			} else if (isImageContent(block)) {
				attachedImageCount += 1;
				const name = `添付画像 ${attachedImageCount}${extensionOf(block.source.media_type)}`;
				const target = { messageId: message.id, blockIndex, nestedIndex: null };
				files.push(imageFile(`${message.id}#image-${attachedImageCount}`, name, block, target));
			} else if (isDocumentContent(block)) {
				files.push(documentFile(`${message.id}#${blockIndex}`, block));
			} else if (isToolResultContent(block) && Array.isArray(block.content)) {
				const toolUse = toolUseById.get(block.tool_use_id);
				for (const [nestedIndex, nested] of block.content.entries()) {
					const key = `${block.tool_use_id}#${nestedIndex}`;
					if (nested.type === "image") {
						const name =
							toolInputFileName(toolUse) ?? `画像${extensionOf(nested.source.media_type)}`;
						const target = { messageId: message.id, blockIndex, nestedIndex };
						files.push(imageFile(key, name, nested, target));
					} else if (nested.type === "document") {
						files.push(documentFile(key, nested));
					}
				}
			} else if (isUploadedFileContent(block)) {
				const key = `${message.id}#${blockIndex}`;
				files.push({ kind: "uploaded", key, ...fromUploadedFileBlock(block) });
			}
		}
	}
	return files;
}
